/** Layout for generated desk signup PDFs. Keep in sync with signupSheetPdf.ts. */

export const SIGNUP_SHEET_PAGE = {
  width: 612,
  height: 792,
  frameInset: 20,
  margin: 32,
  header: 46,
  footer: 22,
  row: 23,
  laneBar: 18,
  teamBar: 26,
  groupGap: 10,
  singlesGap: 6,
  titleLine: 18,
  subtitleLine: 16,
  squadLine: 22,
  bodyPad: 6,
  indexCol: 18,
  laneCol: 36,
  avgCol: 40,
  brkCol: 36,
  usbcCol: 86,
  phoneCol: 70,
  tickCol: 26,
  doublesLaneWriteX: 64,
  doublesLaneWriteW: 40,
};

export const SIGNUP_PAID_LABEL = "Paid";
export const SIGNUP_PAID_TICK_COLUMNS = 1;

export const signupSheetPaidColWidth = () =>
  SIGNUP_PAID_TICK_COLUMNS * SIGNUP_SHEET_PAGE.tickCol;

export const signupSheetBracketColsWidth = ({
  showHBrk = false,
  showSBrk = false,
} = {}) =>
  ((showHBrk ? 1 : 0) + (showSBrk ? 1 : 0)) * SIGNUP_SHEET_PAGE.brkCol;

export const isSignupSheetFiducialToken = (value) => {
  const raw = String(value || "")
    .toUpperCase()
    .replace(/[^A-Z+]/g, "");
  return (
    raw === "PADNW" ||
    raw === "PADNE" ||
    raw === "PADSW" ||
    raw === "PADSE" ||
    raw === "+TL" ||
    raw === "+TR" ||
    raw === "+BL" ||
    raw === "+BR" ||
    raw === "TL+" ||
    raw === "TR+" ||
    raw === "BL+" ||
    raw === "BR+"
  );
};

export const signupSheetBodyTop = () =>
  SIGNUP_SHEET_PAGE.margin +
  SIGNUP_SHEET_PAGE.titleLine +
  SIGNUP_SHEET_PAGE.subtitleLine +
  SIGNUP_SHEET_PAGE.squadLine +
  SIGNUP_SHEET_PAGE.header +
  SIGNUP_SHEET_PAGE.bodyPad;

export const signupSheetKindLayout = (teamSize) => {
  const size = Math.max(1, Number(teamSize) || 1);
  const showLaneColumn = size === 1;
  const showTeamName = size > 2;
  return { teamSize: size, showLaneColumn, showTeamName };
};

export const signupSheetGroupSize = ({
  teamSize,
  showLaneColumn,
  showTeamName,
}) => {
  const layout = signupSheetKindLayout(teamSize);
  const laneColumn = showLaneColumn ?? layout.showLaneColumn;
  const teamName = showTeamName ?? layout.showTeamName;
  const gap = laneColumn
    ? SIGNUP_SHEET_PAGE.singlesGap
    : SIGNUP_SHEET_PAGE.groupGap;
  const bar = laneColumn
    ? 0
    : teamName
      ? SIGNUP_SHEET_PAGE.teamBar
      : SIGNUP_SHEET_PAGE.laneBar;
  const rows = Math.max(1, layout.teamSize) * SIGNUP_SHEET_PAGE.row;
  return {
    bar,
    gap,
    row: SIGNUP_SHEET_PAGE.row,
    content: bar + rows,
    height: bar + rows + gap,
  };
};

export const signupSheetSlotsPerPage = (options) => {
  const { height } = signupSheetGroupSize(options);
  const available =
    SIGNUP_SHEET_PAGE.height -
    SIGNUP_SHEET_PAGE.margin -
    SIGNUP_SHEET_PAGE.footer -
    signupSheetBodyTop();
  return Math.max(1, Math.floor(available / height));
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));

export const unitOcrBox = (box, dimensions) => {
  if (!box) return null;
  const topLeftX = Number(box.topLeftX);
  const topLeftY = Number(box.topLeftY);
  const bottomRightX = Number(box.bottomRightX);
  const bottomRightY = Number(box.bottomRightY);
  if (
    ![topLeftX, topLeftY, bottomRightX, bottomRightY].every(Number.isFinite)
  ) {
    return null;
  }
  const max = Math.max(topLeftX, topLeftY, bottomRightX, bottomRightY);
  const width = Number(dimensions?.width);
  const height = Number(dimensions?.height);
  let left = topLeftX;
  let top = topLeftY;
  let right = bottomRightX;
  let bottom = bottomRightY;
  if (max > 1.5 && max <= 100) {
    left /= 100;
    top /= 100;
    right /= 100;
    bottom /= 100;
  } else if (max > 1.5 && width > 1.5 && height > 1.5) {
    left /= width;
    top /= height;
    right /= width;
    bottom /= height;
  } else if (max > 1.5) {
    return null;
  }
  const next = {
    ...box,
    topLeftX: clamp01(Math.min(left, right)),
    topLeftY: clamp01(Math.min(top, bottom)),
    bottomRightX: clamp01(Math.max(left, right)),
    bottomRightY: clamp01(Math.max(top, bottom)),
  };
  if (
    next.bottomRightX - next.topLeftX <= 0 ||
    next.bottomRightY - next.topLeftY <= 0
  ) {
    return null;
  }
  return next;
};

const stripTableTags = (html) =>
  String(html || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();

export const htmlTableRowTexts = (html) => {
  const source = String(html || "");
  if (!/<table/i.test(source)) return [];
  const rows = [];
  const rowPattern = /<tr[\s\S]*?<\/tr>/gi;
  let rowMatch = rowPattern.exec(source);
  while (rowMatch) {
    const text = stripTableTags(rowMatch[0]);
    if (text) rows.push(text);
    rowMatch = rowPattern.exec(source);
  }
  return rows;
};

export const htmlTableCellRows = (html) => {
  const source = String(html || "");
  if (!/<table/i.test(source)) return [];
  const rows = [];
  const rowPattern = /<tr[\s\S]*?<\/tr>/gi;
  let rowMatch = rowPattern.exec(source);
  while (rowMatch) {
    const cells = [];
    const cellPattern = /<(td|th)([^>]*)>([\s\S]*?)<\/\1>/gi;
    let cellMatch = cellPattern.exec(rowMatch[0]);
    while (cellMatch) {
      const colspan = Number(
        (cellMatch[2].match(/colspan=["']?(\d+)/i) || [])[1] || 1
      );
      const text = stripTableTags(cellMatch[3]);
      cells.push(text);
      for (let extra = 1; extra < colspan; extra += 1) {
        cells.push("");
      }
      cellMatch = cellPattern.exec(rowMatch[0]);
    }
    if (cells.some((cell) => cell.length > 0)) rows.push(cells);
    rowMatch = rowPattern.exec(source);
  }
  return rows;
};

export const sheetBoxForTableRow = (tableBox, rowIndex, rowCount, options = {}) => {
  const rows = options.rows;
  if (Array.isArray(rows) && rows.length === Number(rowCount)) {
    return stackedTableRowBox(tableBox, rows, rowIndex, options.dimensions);
  }
  const count = Number(rowCount);
  const index = Number(rowIndex);
  if (!tableBox || !(count > 0) || !Number.isInteger(index) || index < 0 || index >= count) {
    return null;
  }
  const top = Number(tableBox.topLeftY);
  const bottom = Number(tableBox.bottomRightY);
  const left = Number(tableBox.topLeftX);
  const right = Number(tableBox.bottomRightX);
  const height = bottom - top;
  if (
    ![top, bottom, left, right, height].every(Number.isFinite) ||
    height <= 0 ||
    right <= left
  ) {
    return null;
  }
  return {
    topLeftX: left,
    topLeftY: top + (index / count) * height,
    bottomRightX: right,
    bottomRightY: top + ((index + 1) / count) * height,
    source: "table",
  };
};

export const signupSheetTableRowKind = (text) => {
  const raw = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  const normalized = raw.toLowerCase();
  if (!normalized) return "empty";
  if (/#\s*name/.test(normalized) || (/\bname\b/.test(normalized) && /\bavg\b/.test(normalized))) {
    return "header";
  }
  if (/^lane\b/.test(normalized) && !/[a-z]{3,}\s+[a-z]{3,}/.test(normalized.replace(/^lane\b/, ""))) {
    return "lane";
  }
  if (/^team\s*name\b/.test(normalized)) return "team";
  if (/^[\d\s✓✔xX☑☐]+$/.test(raw)) return "empty";
  return "name";
};

export const tableRowMatchesName = (row, name) => {
  const text = String(row || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const needle = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text || !needle) return false;
  if (text.includes(needle)) return true;
  const tokens = needle.split(" ").filter((token) => token.length >= 2);
  if (tokens.length < 2) return false;
  if (tokens.every((token) => text.includes(token))) return true;
  const flipped = `${tokens[tokens.length - 1]} ${tokens.slice(0, -1).join(" ")}`;
  return text.includes(flipped.replace(/\s+/g, " ").trim());
};

export const bowlerNameFromTableRow = (text) => {
  if (signupSheetTableRowKind(text) !== "name") return "";
  let rest = String(text || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\d{1,2}\s+/, "")
    .replace(/\s+\d{2,3}(\.\d+)?\s*$/, "")
    .replace(/(\s+[A-Z]{2,4})+$/g, "")
    .replace(/\s+[xX✓✔☑☐]+/g, "")
    .trim();
  if (!rest || rest.length < 2) return "";
  if (/^(avg|name|paid|lane|team)$/i.test(rest)) return "";
  return rest;
};

export const namesFromSignupTableGroup = (
  rows,
  { teamName = "", firstBowler = "", teamSize = 4 } = {}
) => {
  const size = Math.max(1, Number(teamSize) || 1);
  const list = rows || [];
  const kinds = list.map((row) => signupSheetTableRowKind(row));
  let start = -1;
  if (teamName) {
    start = list.findIndex(
      (row, index) =>
        (kinds[index] === "team" || kinds[index] === "name") &&
        tableRowMatchesName(row, teamName)
    );
  }
  if (start < 0 && firstBowler) {
    const nameIndex = list.findIndex(
      (row, index) =>
        kinds[index] === "name" && tableRowMatchesName(row, firstBowler)
    );
    if (nameIndex >= 0) {
      start = nameIndex;
      while (start > 0 && kinds[start - 1] === "name") start -= 1;
    }
  }
  if (start < 0) return [];
  let index = start;
  while (index < list.length) {
    const kind = kinds[index];
    if (kind === "team" || kind === "lane") {
      index += 1;
      continue;
    }
    if (teamName && kind === "name" && tableRowMatchesName(list[index], teamName)) {
      index += 1;
      continue;
    }
    break;
  }
  const names = [];
  for (; index < list.length && names.length < size; index += 1) {
    if (kinds[index] === "team" || kinds[index] === "header") break;
    if (kinds[index] === "lane" && names.length) break;
    if (kinds[index] === "lane") continue;
    if (teamName && tableRowMatchesName(list[index], teamName)) continue;
    const name = bowlerNameFromTableRow(list[index]);
    if (name) names.push(name);
  }
  return names;
};

const signupSheetTableRowWeight = (rows, index) => {
  const kind = signupSheetTableRowKind(rows[index]);
  if (kind === "header") {
    return SIGNUP_SHEET_PAGE.header + SIGNUP_SHEET_PAGE.bodyPad;
  }
  if (kind === "team") return SIGNUP_SHEET_PAGE.teamBar;
  if (kind === "lane") return SIGNUP_SHEET_PAGE.laneBar;
  const next = rows[index + 1];
  const nextKind = next != null ? signupSheetTableRowKind(next) : "";
  if (!next || nextKind === "lane" || nextKind === "team" || nextKind === "header") {
    return SIGNUP_SHEET_PAGE.row + SIGNUP_SHEET_PAGE.groupGap;
  }
  return SIGNUP_SHEET_PAGE.row;
};

const stackedTableRowBox = (tableBox, rows, rowIndex, dimensions) => {
  const index = Number(rowIndex);
  if (
    !tableBox ||
    !Array.isArray(rows) ||
    !Number.isInteger(index) ||
    index < 0 ||
    index >= rows.length
  ) {
    return null;
  }
  const pageHeight = Number(dimensions?.height);
  const tableTop = Number(tableBox.topLeftY);
  const tableBottom = Number(tableBox.bottomRightY);
  const left = Number(tableBox.topLeftX);
  const right = Number(tableBox.bottomRightX);
  if (![tableTop, tableBottom, left, right].every(Number.isFinite) || right <= left) {
    return null;
  }
  const tableHeight = tableBottom - tableTop;
  if (!(tableHeight > 0)) return null;
  const titleBand =
    (SIGNUP_SHEET_PAGE.margin +
      SIGNUP_SHEET_PAGE.titleLine +
      SIGNUP_SHEET_PAGE.subtitleLine +
      SIGNUP_SHEET_PAGE.squadLine) /
    SIGNUP_SHEET_PAGE.height;
  const tableStartsAtPageTop =
    Number.isFinite(pageHeight) && pageHeight > 1.5
      ? tableTop <= 0.05 * pageHeight
      : tableTop <= 0.05;
  let bodyTop = tableTop;
  if (tableStartsAtPageTop) {
    bodyTop += titleBand * tableHeight;
  }
  bodyTop = Math.min(Math.max(bodyTop, tableTop), tableBottom);
  const bodyHeight = tableBottom - bodyTop;
  if (!(bodyHeight > 0)) return null;
  const weights = rows.map((_, row) => signupSheetTableRowWeight(rows, row));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (!(total > 0)) return null;
  const before = weights.slice(0, index).reduce((sum, weight) => sum + weight, 0);
  return {
    topLeftX: left,
    topLeftY: bodyTop + (before / total) * bodyHeight,
    bottomRightX: right,
    bottomRightY: bodyTop + ((before + weights[index]) / total) * bodyHeight,
    source: "table",
  };
};

export const sheetBoxForTableName = (tableBox, rows, name, dimensions) => {
  if (!Array.isArray(rows) || !rows.length) return null;
  const index = rows.findIndex((row) => tableRowMatchesName(row, name));
  if (index < 0) return null;
  const slice = sheetBoxForTableRow(tableBox, index, rows.length, {
    rows,
    dimensions,
  });
  if (!slice) return null;
  if (index > 0 && ["lane", "team"].includes(signupSheetTableRowKind(rows[index - 1]))) {
    const lane = sheetBoxForTableRow(tableBox, index - 1, rows.length, {
      rows,
      dimensions,
    });
    if (lane) slice.topLeftY = Math.min(slice.topLeftY, lane.topLeftY);
  }
  return slice;
};

const readTiffOrientation = (data, tiffStart) => {
  if (tiffStart + 8 > data.length) return 1;
  const little = data[tiffStart] === 0x49 && data[tiffStart + 1] === 0x49;
  const big = data[tiffStart] === 0x4d && data[tiffStart + 1] === 0x4d;
  if (!little && !big) return 1;
  const u16 = (index) =>
    little
      ? data[index] | (data[index + 1] << 8)
      : (data[index] << 8) | data[index + 1];
  const u32 = (index) =>
    little
      ? data[index] |
        (data[index + 1] << 8) |
        (data[index + 2] << 16) |
        (data[index + 3] << 24)
      : (data[index] << 24) |
        (data[index + 1] << 16) |
        (data[index + 2] << 8) |
        data[index + 3];
  if (u16(tiffStart + 2) !== 42) return 1;
  const ifd = tiffStart + u32(tiffStart + 4);
  if (ifd < tiffStart || ifd + 2 > data.length) return 1;
  const count = u16(ifd);
  for (let index = 0; index < count; index += 1) {
    const entry = ifd + 2 + index * 12;
    if (entry + 12 > data.length) break;
    if (u16(entry) !== 0x0112) continue;
    const type = u16(entry + 2);
    const value = type === 3 ? u16(entry + 8) : u32(entry + 8);
    if (value >= 1 && value <= 8) return value;
  }
  return 1;
};

export const jpegExifOrientation = (bytes) => {
  const data =
    bytes instanceof Uint8Array
      ? bytes
      : bytes
        ? new Uint8Array(bytes)
        : null;
  if (!data || data.length < 4 || data[0] !== 0xff || data[1] !== 0xd8) {
    return 1;
  }
  let offset = 2;
  while (offset + 4 < data.length) {
    if (data[offset] !== 0xff) break;
    const marker = data[offset + 1];
    if (marker === 0xda || marker === 0xd9) break;
    const length = (data[offset + 2] << 8) | data[offset + 3];
    if (length < 2) break;
    if (marker === 0xe1 && offset + 4 + 6 < data.length) {
      const start = offset + 4;
      if (
        data[start] === 0x45 &&
        data[start + 1] === 0x78 &&
        data[start + 2] === 0x69 &&
        data[start + 3] === 0x66 &&
        data[start + 4] === 0 &&
        data[start + 5] === 0
      ) {
        return readTiffOrientation(data, start + 6);
      }
    }
    offset += 2 + length;
  }
  return 1;
};

export const orientedPageSize = (width, height, orientation) => {
  const pageWidth = Number(width);
  const pageHeight = Number(height);
  if (![5, 6, 7, 8].includes(Number(orientation))) {
    return { width: pageWidth, height: pageHeight };
  }
  return { width: pageHeight, height: pageWidth };
};

export const mapOrientedPoint = (x, y, width, height, orientation) => {
  const pageWidth = Number(width);
  const pageHeight = Number(height);
  switch (Number(orientation) || 1) {
    case 2:
      return { x: pageWidth - x, y };
    case 3:
      return { x: pageWidth - x, y: pageHeight - y };
    case 4:
      return { x, y: pageHeight - y };
    case 5:
      return { x: y, y: x };
    case 6:
      return { x: pageHeight - y, y: x };
    case 7:
      return { x: pageHeight - y, y: pageWidth - x };
    case 8:
      return { x: y, y: pageWidth - x };
    default:
      return { x, y };
  }
};

export const orientSheetBox = (box, width, height, orientation) => {
  if (!box || !orientation || Number(orientation) === 1) return box;
  const corners = [
    mapOrientedPoint(box.topLeftX, box.topLeftY, width, height, orientation),
    mapOrientedPoint(box.bottomRightX, box.topLeftY, width, height, orientation),
    mapOrientedPoint(box.topLeftX, box.bottomRightY, width, height, orientation),
    mapOrientedPoint(
      box.bottomRightX,
      box.bottomRightY,
      width,
      height,
      orientation
    ),
  ];
  return {
    ...box,
    topLeftX: Math.min(...corners.map((corner) => corner.x)),
    topLeftY: Math.min(...corners.map((corner) => corner.y)),
    bottomRightX: Math.max(...corners.map((corner) => corner.x)),
    bottomRightY: Math.max(...corners.map((corner) => corner.y)),
  };
};

export const inferOcrOrientation = (pages) => {
  const page = Array.isArray(pages) ? pages[0] : pages;
  const width = Number(page?.dimensions?.width);
  const height = Number(page?.dimensions?.height);
  const blocks = (page?.blocks || []).filter(
    (block) =>
      Number.isFinite(Number(block?.topLeftX)) &&
      Number.isFinite(Number(block?.bottomRightX)) &&
      Number.isFinite(Number(block?.topLeftY)) &&
      Number.isFinite(Number(block?.bottomRightY))
  );
  if (!(width > 1.5 && height > 1.5) || blocks.length < 3) return 1;
  const tall = blocks.filter(
    (block) =>
      Number(block.bottomRightY) - Number(block.topLeftY) >
      Number(block.bottomRightX) - Number(block.topLeftX)
  );
  if (!(width > height) || tall.length < blocks.length * 0.6) return 1;
  const title =
    blocks.find((block) => String(block.type || "").toLowerCase() === "title") ||
    blocks[0];
  const midX =
    (Number(title.topLeftX) + Number(title.bottomRightX)) / 2;
  return midX < width / 2 ? 6 : 8;
};

export const resolveOcrOrientation = ({ bytes, pages } = {}) => {
  const exif = jpegExifOrientation(bytes);
  const page = Array.isArray(pages) ? pages[0] : pages;
  const width = Number(page?.dimensions?.width);
  const height = Number(page?.dimensions?.height);
  const swaps = [5, 6, 7, 8].includes(exif);
  if (swaps && width > 1.5 && height > 1.5) {
    const oriented = orientedPageSize(width, height, exif);
    if (
      Math.abs(width - oriented.width) < 2 &&
      Math.abs(height - oriented.height) < 2
    ) {
      return 1;
    }
    return exif;
  }
  if (exif >= 2 && exif <= 8) return exif;
  return inferOcrOrientation(pages);
};

export const orientOcrPages = (pages, orientation) => {
  const value = Number(orientation) || 1;
  if (value === 1) return pages || [];
  return (pages || []).map((page) => {
    const width = Number(page?.dimensions?.width);
    const height = Number(page?.dimensions?.height);
    const size = orientedPageSize(width, height, value);
    return {
      ...page,
      dimensions: page?.dimensions
        ? { ...page.dimensions, width: size.width, height: size.height }
        : { width: size.width, height: size.height },
      blocks: (page.blocks || []).map((block) => {
        const oriented = orientSheetBox(block, width, height, value);
        return oriented ? { ...block, ...oriented } : block;
      }),
    };
  });
};

export const signupSheetNormBox = (left, top, right, bottom) => ({
  topLeftX: clamp01(left / SIGNUP_SHEET_PAGE.width),
  topLeftY: clamp01(top / SIGNUP_SHEET_PAGE.height),
  bottomRightX: clamp01(right / SIGNUP_SHEET_PAGE.width),
  bottomRightY: clamp01(bottom / SIGNUP_SHEET_PAGE.height),
});

const slotTop = (slotIndex, options) =>
  signupSheetBodyTop() +
  Math.max(0, Number(slotIndex) || 0) * signupSheetGroupSize(options).height;

export const signupSheetGroupBox = ({
  teamSize,
  slotIndex = 0,
  showLaneColumn,
  showTeamName,
}) => {
  const options = { teamSize, showLaneColumn, showTeamName };
  const size = signupSheetGroupSize(options);
  const top = slotTop(slotIndex, options);
  return signupSheetNormBox(
    SIGNUP_SHEET_PAGE.margin,
    top,
    SIGNUP_SHEET_PAGE.width - SIGNUP_SHEET_PAGE.margin,
    top + size.content
  );
};

export const signupSheetBowlerBox = ({
  teamSize,
  slotIndex = 0,
  bowlerIndex = 0,
  showLaneColumn,
  showTeamName,
}) => {
  const options = { teamSize, showLaneColumn, showTeamName };
  const size = signupSheetGroupSize(options);
  const top =
    slotTop(slotIndex, options) +
    size.bar +
    Math.max(0, Number(bowlerIndex) || 0) * size.row;
  return signupSheetNormBox(
    SIGNUP_SHEET_PAGE.margin,
    top,
    SIGNUP_SHEET_PAGE.width - SIGNUP_SHEET_PAGE.margin,
    top + size.row
  );
};

export const signupSheetHighlightBox = ({
  teamSize,
  slotIndex = 0,
  bowlerIndex = null,
  showLaneColumn,
  showTeamName,
}) => {
  const box =
    bowlerIndex == null || bowlerIndex === ""
      ? signupSheetGroupBox({
          teamSize,
          slotIndex,
          showLaneColumn,
          showTeamName,
        })
      : signupSheetBowlerBox({
          teamSize,
          slotIndex,
          bowlerIndex,
          showLaneColumn,
          showTeamName,
        });
  return { ...box, source: "layout" };
};

export const mapUnitBoxToRegion = (unit, region) => {
  if (!unit || !region) return null;
  const width = Number(region.bottomRightX) - Number(region.topLeftX);
  const height = Number(region.bottomRightY) - Number(region.topLeftY);
  if (!(width > 0) || !(height > 0)) return null;
  const left = Number(unit.topLeftX);
  const top = Number(unit.topLeftY);
  const right = Number(unit.bottomRightX);
  const bottom = Number(unit.bottomRightY);
  if (![left, top, right, bottom].every(Number.isFinite)) return null;
  return {
    topLeftX: Number(region.topLeftX) + left * width,
    topLeftY: Number(region.topLeftY) + top * height,
    bottomRightX: Number(region.topLeftX) + right * width,
    bottomRightY: Number(region.topLeftY) + bottom * height,
    source: unit.source || "layout",
  };
};

export const SIGNUP_SHEET_PHOTO_PAD = {
  topLeftX: 0.04,
  topLeftY: 0.05,
  bottomRightX: 0.96,
  bottomRightY: 0.97,
};

export const signupSheetPhotoHighlightBox = ({
  teamSize = 2,
  slotIndex = 0,
  bowlerIndex = null,
  pad = SIGNUP_SHEET_PHOTO_PAD,
} = {}) =>
  mapUnitBoxToRegion(
    signupSheetHighlightBox({ teamSize, slotIndex, bowlerIndex }),
    pad
  );

export const signupSheetLaneBox = ({
  teamSize,
  slotIndex = 0,
  showLaneColumn,
  showTeamName,
  showUsbc = false,
  showEmail = false,
  showPhone = false,
  showHBrk = false,
  showSBrk = false,
  tickCount = 0,
}) => {
  const options = { teamSize, showLaneColumn, showTeamName };
  const layout = signupSheetKindLayout(teamSize);
  const laneColumn = showLaneColumn ?? layout.showLaneColumn;
  const teamName = showTeamName ?? layout.showTeamName;
  const top = slotTop(slotIndex, options);
  const size = signupSheetGroupSize(options);

  if (laneColumn) {
    const contentWidth =
      SIGNUP_SHEET_PAGE.width - SIGNUP_SHEET_PAGE.margin * 2;
    let fixed =
      SIGNUP_SHEET_PAGE.indexCol +
      SIGNUP_SHEET_PAGE.avgCol +
      SIGNUP_SHEET_PAGE.laneCol +
      signupSheetPaidColWidth() +
      signupSheetBracketColsWidth({ showHBrk, showSBrk }) +
      Math.max(0, Number(tickCount) || 0) * SIGNUP_SHEET_PAGE.tickCol;
    if (showUsbc) fixed += SIGNUP_SHEET_PAGE.usbcCol;
    if (showPhone) fixed += SIGNUP_SHEET_PAGE.phoneCol;
    const flexCount = 1 + (showEmail ? 1 : 0);
    const flexWidth = Math.max(72, (contentWidth - fixed) / flexCount);
    const left =
      SIGNUP_SHEET_PAGE.margin +
      SIGNUP_SHEET_PAGE.indexCol +
      signupSheetPaidColWidth() +
      flexWidth;
    return signupSheetNormBox(
      left,
      top,
      left + SIGNUP_SHEET_PAGE.laneCol,
      top + size.row
    );
  }

  if (teamName) {
    const left = SIGNUP_SHEET_PAGE.width - SIGNUP_SHEET_PAGE.margin - 62;
    return signupSheetNormBox(left, top, left + 40, top + size.bar);
  }

  return signupSheetNormBox(
    SIGNUP_SHEET_PAGE.doublesLaneWriteX,
    top,
    SIGNUP_SHEET_PAGE.doublesLaneWriteX + SIGNUP_SHEET_PAGE.doublesLaneWriteW,
    top + size.bar
  );
};

export const isUnitSheetBox = (box) => {
  if (!box) return false;
  const max = Math.max(
    Number(box.topLeftX) || 0,
    Number(box.topLeftY) || 0,
    Number(box.bottomRightX) || 0,
    Number(box.bottomRightY) || 0
  );
  return max <= 1.5;
};

export const sheetBoxPageSize = (boxes, dimensions) => {
  const list = (boxes || []).filter(Boolean);
  if (list.length && list.every((box) => isUnitSheetBox(box))) {
    return { width: 1, height: 1 };
  }
  const width = Number(dimensions?.width);
  const height = Number(dimensions?.height);
  if (width > 1.5 && height > 1.5) return { width, height };
  return { width: 0, height: 0 };
};

export const snapSignupSheetSlot = (box, options) => {
  if (!box || !Number.isFinite(Number(box.topLeftY))) return null;
  if (!isUnitSheetBox(box)) return null;
  const top = Number(box.topLeftY);
  const bottom = Number(box.bottomRightY);
  const midY = Number.isFinite(bottom) ? (top + bottom) / 2 : top;
  const size = signupSheetGroupSize(options);
  const slots = signupSheetSlotsPerPage(options);
  const index = Math.floor(
    (midY * SIGNUP_SHEET_PAGE.height - signupSheetBodyTop()) / size.height
  );
  if (!Number.isInteger(index) || index < 0 || index >= slots) return null;
  const group = signupSheetGroupBox({ ...options, slotIndex: index });
  const groupHeight = group.bottomRightY - group.topLeftY;
  const boxHeight = Number.isFinite(bottom) ? bottom - top : 0;
  if (groupHeight > 0 && boxHeight > groupHeight * 2.5) return null;
  return index;
};

export const isColumnHighlightBox = (box, dimensions) => {
  if (!box) return false;
  const width = Number(box.bottomRightX) - Number(box.topLeftX);
  const height = Number(box.bottomRightY) - Number(box.topLeftY);
  if (![width, height].every(Number.isFinite) || width <= 0 || height <= 0) {
    return false;
  }
  if (height <= width) return false;
  if (isUnitSheetBox(box)) {
    return height >= 0.4 && width <= 0.22;
  }
  const page = sheetBoxPageSize([box], dimensions);
  if (page.width > 1.5 && page.height > 1.5) {
    return height / page.height >= 0.4 && width / page.width <= 0.22;
  }
  return false;
};

export const isNameHighlightBox = (box, dimensions) => {
  if (!box) return false;
  const width = Number(box.bottomRightX) - Number(box.topLeftX);
  const height = Number(box.bottomRightY) - Number(box.topLeftY);
  if (![width, height].every(Number.isFinite)) return false;
  if (width <= 0 || height <= 0) return false;
  if (isColumnHighlightBox(box, dimensions)) return true;
  if (String(box.source || "") === "table") {
    if (isUnitSheetBox(box)) return height <= 0.12;
    const page = sheetBoxPageSize([box], dimensions);
    if (page.width > 1.5 && page.height > 1.5) {
      return height / page.height <= 0.12;
    }
    return height >= 4 && height <= 140;
  }
  if (isUnitSheetBox(box)) {
    if (height > 0.18) return false;
    return width * height <= 0.14;
  }
  const page = sheetBoxPageSize([box], dimensions);
  if (page.width > 1.5 && page.height > 1.5) {
    return height / page.height <= 0.18 && (width * height) / (page.width * page.height) <= 0.14;
  }
  return height >= 4 && height <= 150 && width >= 8 && width * height <= 50000;
};

const boxHeightOf = (box) =>
  Number(box.bottomRightY) - Number(box.topLeftY);

const medianValue = (values) => {
  const sorted = (values || []).filter((value) => value > 0).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  return sorted[Math.floor(sorted.length / 2)];
};

const dropTallNameOutliers = (boxes, dimensions) => {
  const usable = (boxes || []).filter((box) => isNameHighlightBox(box, dimensions));
  if (usable.length <= 1) return usable;
  const heights = usable.map(boxHeightOf).sort((left, right) => left - right);
  const median = heights[Math.floor(heights.length / 2)] || 0;
  if (!(median > 0)) return usable;
  return usable.filter((box) => boxHeightOf(box) <= median * 2.5);
};

const nameBoxesForOneTeam = (boxes, teamSize, unit) => {
  const size = Math.max(1, Number(teamSize) || 1);
  if (boxes.length <= 1) return boxes;
  const sorted = [...boxes].sort(
    (left, right) => Number(left.topLeftY) - Number(right.topLeftY)
  );
  const rowH = medianValue(sorted.map(boxHeightOf)) || (unit ? 0.028 : 24);
  const maxSpan = rowH * (size + 2);
  const span =
    Number(sorted[sorted.length - 1].bottomRightY) - Number(sorted[0].topLeftY);
  if (!(span > maxSpan)) return sorted;
  const lowestBottom = Number(sorted[sorted.length - 1].bottomRightY);
  return sorted.filter(
    (box) => lowestBottom - Number(box.topLeftY) <= maxSpan
  );
};

const recapStyleSignupBox = ({
  left,
  top,
  right,
  bottom,
  page,
  unit,
}) => {
  const padX = unit ? 0.006 : page.width > 1.5 ? 0.006 * page.width : 6;
  const padTop = unit ? 0.008 : page.height > 1.5 ? 0.008 * page.height : 8;
  const padBottom = unit ? 0.006 : page.height > 1.5 ? 0.006 * page.height : 6;
  const maxX = unit ? 1 : page.width > 1.5 ? page.width : right + padX;
  const maxY = unit ? 1 : page.height > 1.5 ? page.height : bottom + padBottom;
  const clamp = (value, max) => Math.min(max, Math.max(0, value));
  return {
    topLeftX: clamp(left - padX, maxX),
    topLeftY: clamp(top - padTop, maxY),
    bottomRightX: clamp(right + padX, maxX),
    bottomRightY: clamp(bottom + padBottom, maxY),
    source: "names",
  };
};

const doublesNameHighlightBox = ({
  usable,
  includeLaneBar,
  page,
  unit,
  left,
  right,
}) => {
  const tops = usable
    .map((box) => Number(box.topLeftY))
    .filter((value) => Number.isFinite(value))
    .sort((leftTop, rightTop) => leftTop - rightTop);
  if (!tops.length) return null;
  const firstTop = tops[0];
  const lastTop = tops[tops.length - 1];
  const heights = usable
    .map(boxHeightOf)
    .filter((value) => value > 0)
    .sort((leftH, rightH) => leftH - rightH);
  const minPitch = unit ? 0.008 : 6;
  const pitch =
    lastTop - firstTop > minPitch
      ? lastTop - firstTop
      : heights[0] || (unit ? 0.028 : 24);
  const bodyBottom = lastTop + pitch * 1.05;
  const laneLift = includeLaneBar
    ? pitch * (SIGNUP_SHEET_PAGE.laneBar / SIGNUP_SHEET_PAGE.row)
    : 0;
  const box = recapStyleSignupBox({
    left,
    top: firstTop - laneLift,
    right,
    bottom: bodyBottom,
    page,
    unit,
  });
  const clamp = (value, max) => Math.min(max, Math.max(0, value));
  if (unit || page.width > 1.5) {
    return {
      ...box,
      topLeftX: clamp(left - 0.06 * page.width, page.width),
      bottomRightX: 0.98 * page.width,
    };
  }
  return box;
};

export const pairHighlightFromNameBoxes = (
  boxes,
  { teamSize = 2, singleRow = false, dimensions, includeLaneBar = true } = {}
) => {
  const size = Math.max(1, Number(teamSize) || 1);
  const filtered = dropTallNameOutliers(boxes, dimensions).filter(
    (box) => size < 3 || !isColumnHighlightBox(box, dimensions)
  );
  if (!filtered.length) return null;
  const unitGuess = isUnitSheetBox(filtered[0]);
  const usable = singleRow
    ? filtered
    : nameBoxesForOneTeam(filtered, size, unitGuess);
  if (!usable.length) return null;
  const page = sheetBoxPageSize(usable, dimensions);
  const top = Math.min(...usable.map((box) => Number(box.topLeftY)));
  let bottom = Math.max(...usable.map((box) => Number(box.bottomRightY)));
  const left = Math.min(...usable.map((box) => Number(box.topLeftX)));
  const right = Math.max(...usable.map((box) => Number(box.bottomRightX)));
  const unit = isUnitSheetBox(usable[0]);
  if (!singleRow && size >= 3) {
    const span = Math.max(bottom - top, unit ? 0.016 : 8);
    const rowGuess =
      medianValue(usable.map(boxHeightOf)) || (unit ? 0.028 : 24);
    if (usable.length === 1 && span < rowGuess * 2.2) {
      bottom = top + span * (size + 0.15);
    }
    const box = recapStyleSignupBox({
      left,
      top: top - rowGuess * 1.25,
      right,
      bottom,
      page,
      unit,
    });
    return box;
  }
  if (!singleRow && size === 2) {
    const box = doublesNameHighlightBox({
      usable,
      includeLaneBar,
      page,
      unit,
      left,
      right,
    });
    if (box) return box;
  }
  const columns = usable.filter((box) => isColumnHighlightBox(box, dimensions));
  if (columns.length || usable.some((box) => box.source === "table")) {
    const unit = isUnitSheetBox(usable[0]);
    const padX = unit ? 0.012 : page.width > 1.5 ? 0.012 * page.width : 4;
    const maxX = unit ? 1 : page.width > 1.5 ? page.width : right + padX;
    const maxY = unit ? 1 : page.height > 1.5 ? page.height : bottom;
    const clamp = (value, max) => Math.min(max, Math.max(0, value));
    return {
      topLeftX: clamp(left - padX, maxX),
      topLeftY: clamp(top, maxY),
      bottomRightX: clamp(right + padX, maxX),
      bottomRightY: clamp(bottom, maxY),
      source: "names",
    };
  }
  if (!(page.width > 1.5) && !isUnitSheetBox(usable[0])) {
    const nameWidth = Math.max(
      ...usable.map(
        (box) => Number(box.bottomRightX) - Number(box.topLeftX)
      ),
      8
    );
    const span = Math.max(bottom - top, 8);
    const pairSize = Math.max(2, size);
    if (
      !singleRow &&
      usable.length === 1 &&
      pairSize >= 2 &&
      span < 90
    ) {
      bottom = top + Math.max(span * (pairSize + 0.25), 24 * pairSize);
    }
    const height = Math.max(bottom - top, 16);
    const padTop = includeLaneBar
      ? Math.max(height * 0.45, 8)
      : Math.max(height * 0.12, 3);
    const padBottom = includeLaneBar
      ? Math.max(height * 0.08, 4)
      : Math.max(height * 0.35, 8);
    return {
      topLeftX: Math.max(0, left - nameWidth * 0.2),
      topLeftY: Math.max(0, top - padTop),
      bottomRightX: right + nameWidth * 2.4,
      bottomRightY: bottom + padBottom,
      source: "names",
    };
  }
  const clamp = (value, max) => Math.min(max, Math.max(0, value));
  const span = Math.max(bottom - top, 0.016 * page.height);
  const pairSize = Math.max(2, size);
  if (
    !singleRow &&
    usable.length === 1 &&
    pairSize >= 2 &&
    span < 0.045 * page.height
  ) {
    bottom = top + Math.max(span * (pairSize + 0.25), 0.022 * page.height * pairSize);
  }
  const height = Math.max(bottom - top, 0.02 * page.height);
  const padTop = includeLaneBar
    ? Math.min(0.05 * page.height, Math.max(height * 0.45, 0.016 * page.height))
    : Math.min(0.008 * page.height, Math.max(height * 0.12, 0.003 * page.height));
  const padBottom = includeLaneBar
    ? Math.min(0.01 * page.height, Math.max(height * 0.08, 0.004 * page.height))
    : Math.min(0.035 * page.height, Math.max(height * 0.35, 0.01 * page.height));
  return {
    topLeftX: clamp(left - 0.06 * page.width, page.width),
    topLeftY: clamp(top - padTop, page.height),
    bottomRightX: 0.98 * page.width,
    bottomRightY: clamp(bottom + padBottom, page.height),
    source: "names",
  };
};

const printedText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const printedBoxArea = (box) =>
  Math.max(0, Number(box.bottomRightX) - Number(box.topLeftX)) *
  Math.max(0, Number(box.bottomRightY) - Number(box.topLeftY));

const isPrintedLaneLabel = (content) => {
  const text = printedText(content);
  return text === "lane" || /^lane\b/.test(text);
};

export const findPrintedLaneLabels = (blocks, dimensions) => {
  const matches = (blocks || [])
    .filter((block) => {
      if (
        !block ||
        !Number.isFinite(Number(block.topLeftX)) ||
        !Number.isFinite(Number(block.topLeftY)) ||
        !Number.isFinite(Number(block.bottomRightX)) ||
        !Number.isFinite(Number(block.bottomRightY)) ||
        !isPrintedLaneLabel(block.content)
      ) {
        return false;
      }
      const area = printedBoxArea(block);
      const height = Number(block.bottomRightY) - Number(block.topLeftY);
      if (isUnitSheetBox(block)) return area > 0 && area < 0.04;
      const page = sheetBoxPageSize([block], dimensions);
      if (page.width > 1.5 && page.height > 1.5) {
        return (
          area / (page.width * page.height) < 0.04 &&
          height / page.height < 0.08
        );
      }
      return area > 8 && area < 12000 && height > 4 && height < 80;
    })
    .sort(
      (left, right) =>
        Number(left.topLeftY) - Number(right.topLeftY) ||
        Number(left.topLeftX) - Number(right.topLeftX)
    );
  const clustered = [];
  const yGap = (left, right) =>
    isUnitSheetBox(left)
      ? 0.018
      : Math.max(
          8,
          (Number(left.bottomRightY) - Number(left.topLeftY)) * 0.8
        );
  matches.forEach((box) => {
    const last = clustered[clustered.length - 1];
    if (last && Math.abs(Number(box.topLeftY) - Number(last.topLeftY)) < yGap(last, box)) {
      return;
    }
    clustered.push({
      topLeftX: Number(box.topLeftX),
      topLeftY: Number(box.topLeftY),
      bottomRightX: Number(box.bottomRightX),
      bottomRightY: Number(box.bottomRightY),
    });
  });
  return clustered;
};

export const slotIndexFromPrintedLanes = (box, lanes) => {
  if (!box || !lanes?.length) return null;
  const y = Number(box.topLeftY);
  if (!Number.isFinite(y)) return null;
  const slop = isUnitSheetBox(box) ? 0.008 : 12;
  let best = 0;
  lanes.forEach((lane, index) => {
    if (Number(lane.topLeftY) <= y + slop) best = index;
  });
  return best;
};

export const highlightFromPrintedLaneSlots = (
  lanes,
  { teamSize = 2, slotIndex = 0, bowlerIndex = null, dimensions } = {}
) => {
  if (!lanes?.length) return null;
  const page = sheetBoxPageSize(lanes, dimensions);
  const size = signupSheetGroupSize({ teamSize });
  const index = Math.min(Math.max(0, Number(slotIndex) || 0), lanes.length - 1);
  const lane = lanes[index];
  const next = lanes[index + 1];
  const prev = lanes[index - 1];
  if (!(page.width > 1.5) && !isUnitSheetBox(lane)) {
    const step = next
      ? Number(next.topLeftY) - Number(lane.topLeftY)
      : prev
        ? Number(lane.topLeftY) - Number(prev.topLeftY)
        : 80;
    if (!(step > 8)) return null;
    const content = step * (size.content / size.height);
    const top = Number(lane.topLeftY);
    const left = Math.max(0, Number(lane.topLeftX) - 20);
    const right = Number(lane.bottomRightX) + 800;
    if (bowlerIndex == null || bowlerIndex === "") {
      return {
        topLeftX: left,
        topLeftY: top,
        bottomRightX: right,
        bottomRightY: top + content,
        source: "layout",
      };
    }
    const bar = content * (size.bar / Math.max(size.content, 1));
    const bodyTop = top + bar;
    const rows = Math.max(1, Number(teamSize) || 1);
    const row = (content - bar) / rows;
    const rowIndex = Math.min(Math.max(0, Number(bowlerIndex) || 0), rows - 1);
    return {
      topLeftX: left,
      topLeftY: bodyTop + row * rowIndex,
      bottomRightX: right,
      bottomRightY: bodyTop + row * (rowIndex + 1),
      source: "layout",
    };
  }
  const clamp = (value, max) => Math.min(max, Math.max(0, value));
  const step = next
    ? Number(next.topLeftY) - Number(lane.topLeftY)
    : prev
      ? Number(lane.topLeftY) - Number(prev.topLeftY)
      : Math.max(size.height / SIGNUP_SHEET_PAGE.height, 0.08) * page.height;
  if (!(step > 0.02 * page.height)) return null;
  const content = step * (size.content / size.height);
  const top = Number(lane.topLeftY);
  const bottom = top + content;
  const left = clamp(Number(lane.topLeftX) - 0.02 * page.width, page.width);
  if (bowlerIndex == null || bowlerIndex === "") {
    return {
      topLeftX: left,
      topLeftY: clamp(top, page.height),
      bottomRightX: 0.97 * page.width,
      bottomRightY: clamp(bottom, page.height),
      source: "layout",
    };
  }
  const bar = content * (size.bar / Math.max(size.content, 1));
  const bodyTop = top + bar;
  const rows = Math.max(1, Number(teamSize) || 1);
  const row = (content - bar) / rows;
  const rowIndex = Math.min(Math.max(0, Number(bowlerIndex) || 0), rows - 1);
  return {
    topLeftX: left,
    topLeftY: clamp(bodyTop + row * rowIndex, page.height),
    bottomRightX: 0.97 * page.width,
    bottomRightY: clamp(bodyTop + row * (rowIndex + 1), page.height),
    source: "layout",
  };
};

export const bowlerHighlightFromPairBox = (
  pairBox,
  bowlerIndex,
  { teamSize = 2 } = {}
) => {
  if (!pairBox) return null;
  const size = signupSheetGroupSize({ teamSize });
  const top = Number(pairBox.topLeftY);
  const bottom = Number(pairBox.bottomRightY);
  const height = bottom - top;
  if (!(height > 0)) return null;
  const bar = height * (size.bar / Math.max(size.content, 1));
  const bodyTop = top + bar;
  const rows = Math.max(1, Number(teamSize) || 1);
  const row = (height - bar) / rows;
  const rowIndex = Math.min(Math.max(0, Number(bowlerIndex) || 0), rows - 1);
  return {
    topLeftX: pairBox.topLeftX,
    topLeftY: bodyTop + row * rowIndex,
    bottomRightX: pairBox.bottomRightX,
    bottomRightY: bodyTop + row * (rowIndex + 1),
    source: pairBox.source || "layout",
  };
};

export const signupSheetHeaderLabelY = () =>
  signupSheetBodyTop() - SIGNUP_SHEET_PAGE.bodyPad - 8;

export const signupSheetHeaderColumns = ({
  teamSize,
  showUsbc = false,
  showEmail = false,
  showPhone = false,
  showHBrk = false,
  showSBrk = false,
  tickCount = 0,
} = {}) => {
  const layout = signupSheetKindLayout(teamSize);
  const contentWidth = SIGNUP_SHEET_PAGE.width - SIGNUP_SHEET_PAGE.margin * 2;
  let fixed =
    SIGNUP_SHEET_PAGE.indexCol +
    SIGNUP_SHEET_PAGE.avgCol +
    signupSheetPaidColWidth() +
    signupSheetBracketColsWidth({ showHBrk, showSBrk }) +
    Math.max(0, Number(tickCount) || 0) * SIGNUP_SHEET_PAGE.tickCol;
  if (layout.showLaneColumn) fixed += SIGNUP_SHEET_PAGE.laneCol;
  if (showUsbc) fixed += SIGNUP_SHEET_PAGE.usbcCol;
  if (showPhone) fixed += SIGNUP_SHEET_PAGE.phoneCol;
  const flexCount = 1 + (showEmail ? 1 : 0);
  const flexWidth = Math.max(72, (contentWidth - fixed) / flexCount);
  const cols = [];
  let x = SIGNUP_SHEET_PAGE.margin;
  const add = (key, width, label) => {
    cols.push({ key, x, width, label });
    x += width;
  };
  add("index", SIGNUP_SHEET_PAGE.indexCol, "#");
  add("paid", signupSheetPaidColWidth(), SIGNUP_PAID_LABEL);
  add("name", flexWidth, "Name");
  if (layout.showLaneColumn) add("lane", SIGNUP_SHEET_PAGE.laneCol, "Lane");
  add("avg", SIGNUP_SHEET_PAGE.avgCol, "Avg");
  if (showHBrk) add("hBrk", SIGNUP_SHEET_PAGE.brkCol, "H-brk");
  if (showSBrk) add("sBrk", SIGNUP_SHEET_PAGE.brkCol, "S-brk");
  if (showUsbc) add("usbc", SIGNUP_SHEET_PAGE.usbcCol, "USBC");
  if (showEmail) add("email", flexWidth, "Email");
  if (showPhone) add("phone", SIGNUP_SHEET_PAGE.phoneCol, "Phone");
  return cols;
};

export const isUsableSheetPad = (pad) => {
  if (!pad) return false;
  const width = Number(pad.bottomRightX) - Number(pad.topLeftX);
  const height = Number(pad.bottomRightY) - Number(pad.topLeftY);
  return (
    [width, height].every(Number.isFinite) &&
    width > 0.35 &&
    height > 0.35 &&
    width < 1.8 &&
    height < 2.4
  );
};

const unitBlocksForPad = (blocks, dimensions) =>
  (blocks || [])
    .map((block) => {
      const box = unitOcrBox(block, dimensions);
      if (!box) return null;
      return {
        ...box,
        content: block.content,
        type: block.type,
      };
    })
    .filter(Boolean);

const SKIP_INK_TYPES = new Set(["image", "figure"]);

export const signupSheetContentBox = () => {
  const insetX = SIGNUP_SHEET_PAGE.margin / SIGNUP_SHEET_PAGE.width;
  const insetY = SIGNUP_SHEET_PAGE.margin / SIGNUP_SHEET_PAGE.height;
  return {
    topLeftX: insetX,
    topLeftY: insetY,
    bottomRightX: 1 - insetX,
    bottomRightY: 1 - insetY,
  };
};

export const signupSheetPrintedInkBox = (blocks, dimensions) => {
  const unit = unitBlocksForPad(blocks, dimensions).filter((block) => {
    const type = String(block.type || "text").toLowerCase();
    if (SKIP_INK_TYPES.has(type)) return false;
    const width = Number(block.bottomRightX) - Number(block.topLeftX);
    const height = Number(block.bottomRightY) - Number(block.topLeftY);
    if (!(width > 0) || !(height > 0)) return false;
    return width * height < 0.92;
  });
  if (!unit.length) return null;
  return {
    topLeftX: Math.min(...unit.map((block) => Number(block.topLeftX))),
    topLeftY: Math.min(...unit.map((block) => Number(block.topLeftY))),
    bottomRightX: Math.max(...unit.map((block) => Number(block.bottomRightX))),
    bottomRightY: Math.max(...unit.map((block) => Number(block.bottomRightY))),
    source: "ink",
  };
};

export const signupSheetPadFromPrintedInk = (blocks, dimensions) => {
  const ink = signupSheetPrintedInkBox(blocks, dimensions);
  if (!ink) return null;
  const inkWidth = ink.bottomRightX - ink.topLeftX;
  const inkHeight = ink.bottomRightY - ink.topLeftY;
  if (inkWidth < 0.45 || inkHeight < 0.55) return null;
  const content = signupSheetContentBox();
  const padWidth = inkWidth / (content.bottomRightX - content.topLeftX);
  const padHeight = inkHeight / (content.bottomRightY - content.topLeftY);
  const pad = {
    topLeftX: ink.topLeftX - content.topLeftX * padWidth,
    topLeftY: ink.topLeftY - content.topLeftY * padHeight,
    bottomRightX: ink.topLeftX - content.topLeftX * padWidth + padWidth,
    bottomRightY: ink.topLeftY - content.topLeftY * padHeight + padHeight,
    source: "ink",
  };
  return isUsableSheetPad(pad) ? pad : null;
};

export const signupSheetPhotoPad = (blocks, dimensions) =>
  signupSheetPadFromPrintedInk(blocks, dimensions);